import { useStore } from '@tanstack/react-form'
import { parseISO } from 'date-fns'
import { AlertTriangle, Clock, Info, Package, X } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import z from 'zod'

import {
  type ClinicId,
  clinicIdSchema,
  formatQuantity,
  type Product,
  type ProductDerivedFields,
} from '@snootsvet/shared'

import {
  Autocomplete,
  AutocompleteCollection,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
} from '@/frontend/components/autocomplete'
import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { useAppForm, withFieldGroup } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { FormError } from '@/frontend/components/form/primitives'
import { zodErrorToFieldErrors } from '@/frontend/components/form/validation'
import { NumericInput } from '@/frontend/components/numeric-input'
import { Button } from '@/frontend/components/ui/button'
import { ProductSearch } from '@/frontend/features/inventory/product-search'
import { useGetProduct } from '@/frontend/hooks/api/use-get-product'
import { usePostStockReceive } from '@/frontend/hooks/api/use-post-stock-receive'
import { hasBatchedStock, productHasExpiryDate, stockRowTextClass } from '@/frontend/lib/stock'
import { cn } from '@/frontend/lib/utils'
import { routes } from '@/frontend/routes'

// The product picker searches the server on demand, so the full catalog is
// never held client-side. We store the selected product on each item so that
// row-level logic can access it without refetching. The schema validates only
// a minimal product interface — the fields needed for submission — not the
// entire product shape. Batch number and expiry are never hard-required: we
// nudge for them via a soft warning derived from the product's stock history
// instead of a product flag.
const itemSchema = z.object({
  product: z.object(
    {
      id: z.string().min(1, 'Please select a product'),
      packSize: z.int(),
    },
    'Please select a product'
  ),
  // Preprocess coerces undefined/null to 0 so we never surface zod's raw
  // "expected number, received undefined" message.
  /** Whole packs received (e.g. 2 boxes). */
  packs: z.preprocess((v) => v ?? 0, z.number().int('Packs must be a whole number').min(0)),
  /** Loose subunits received (e.g. 10 extra tablets on top of whole packs). May be fractional (e.g. 2.5 ml). */
  looseUnits: z.preprocess(
    (v) => v ?? 0,
    z.number().min(0).multipleOf(0.01, 'Maximum 2 decimal places')
  ),
  batchNumber: z.string(),
  expiryDate: z.iso.date().optional(),
})

const formSchema = z
  .object({
    clinicId: z.enum(clinicIdSchema.options, { message: 'Please select a clinic' }),
    items: z.array(itemSchema).min(1, 'Add at least one item'),
  })
  .superRefine((val, ctx) => {
    val.items.forEach((item, i) => {
      if (item.packs <= 0 && item.looseUnits <= 0) {
        const isSingleUnit = (item.product?.packSize ?? 1) === 1
        ctx.addIssue({
          code: 'custom',
          message: isSingleUnit
            ? 'Please enter a quantity'
            : 'Please enter at least 1 unit or subunit',
          path: ['items', i, 'packs'],
        })
      }
    })
  })

// The schema is the strict, submittable contract; the form's live state loosens
// clinicId (and the per-item product) to null so pickers bind cleanly without a
// cast, and so ClinicSearch's region-clear effect can fire onChange(null)
// without leaving a stale value in form state.
type FormValues = {
  clinicId: ClinicId | null
  items: ItemFields[]
}

type ItemFields = {
  product: (Product & ProductDerivedFields) | null
  packs: number
  looseUnits: number
  batchNumber: string
  expiryDate: string | undefined
}

const ITEM_DEFAULTS: ItemFields = {
  product: null,
  packs: 0,
  looseUnits: 0,
  batchNumber: '',
  expiryDate: undefined,
}

/**
 * Grid template for the receive-stock table. The parent uses this on its own
 * grid; each row uses `grid-cols-subgrid` to inherit these tracks so header,
 * input rows, and footer hints all stay aligned automatically.
 */
const GRID_TEMPLATE = 'grid-cols-[minmax(0,28fr)_minmax(0,22fr)_minmax(0,16fr)_minmax(0,28fr)_2rem]'

type BatchSuggestion = {
  batchNumber: string
  expiryDate: string | null
  createdAt: string
  quantity: number
  isExpired: boolean
}

/** Formats an ISO date for compact display in the batch dropdown. */
function formatExpiry(expiryDate: string | null): string | null {
  if (!expiryDate) return null
  return parseISO(expiryDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

/**
 * Converts packs + loose units into total dispensable units for the backend.
 * E.g. 2 boxes of 100 tablets + 10 loose = 210.
 */
function toTotalUnits(packs: number, looseUnits: number, packSize: number): number {
  return packs * packSize + looseUnits
}

/**
 * Single row of the Receive Stock form, scoped to `items[i]` in the parent
 * form via `withFieldGroup`. Owns its own product-with-stock fetch so it can
 * show contextual info and power batch-number autocompletion.
 */
const ItemRow = withFieldGroup({
  defaultValues: ITEM_DEFAULTS,
  props: {
    onRemove: () => {},
    canRemove: true,
    /** Suppresses the top separator on the first row. */
    isFirst: false,
    /** The clinic being received into — used to split stock into "here" vs "other clinics". */
    clinicId: null as ClinicId | null,
  },
  render: ({ group, onRemove, canRemove, isFirst, clinicId }) => {
    const product = useStore(group.store, (s) => s.values.product)
    const batchNumber = useStore(group.store, (s) => s.values.batchNumber)
    const expiryDate = useStore(group.store, (s) => s.values.expiryDate)
    const packs = useStore(group.store, (s) => s.values.packs)
    const looseUnits = useStore(group.store, (s) => s.values.looseUnits)
    const isSingleUnit = (product?.packSize ?? 1) === 1
    const unitLabel = product?.packUnit ?? 'unit'
    const subunitLabel = product?.unit ?? 'subunit'
    const totalUnits = product ? toTotalUnits(packs, looseUnits, product.packSize) : 0

    const { data: productWithStock } = useGetProduct(product?.id, { include: 'stock' })

    // We derive whether batch number / expiry are expected from the product's
    // own stock history rather than a product flag. These drive soft,
    // non-blocking warnings only — never hard validation.
    const productIsBatched = hasBatchedStock(productWithStock?.stock ?? [])
    const productExpires = productHasExpiryDate(productWithStock?.stock ?? [])

    // Distinct existing batches across all clinics, with on-hand quantity
    // (summed across clinics) and expiry status. We surface batches from any
    // clinic since a batch received here previously at another clinic is
    // still a plausible repeat.
    const existingBatches = useMemo<BatchSuggestion[]>(() => {
      if (!productWithStock) return []
      const now = new Date()
      const byBatch = new Map<string, BatchSuggestion>()
      for (const row of productWithStock.stock) {
        if (!row.batchNumber) continue
        const existing = byBatch.get(row.batchNumber)
        if (existing) {
          existing.quantity += row.quantity
          if (row.createdAt > existing.createdAt) existing.createdAt = row.createdAt
        } else {
          const isExpired = !!row.expiryDate && parseISO(row.expiryDate) <= now
          byBatch.set(row.batchNumber, {
            batchNumber: row.batchNumber,
            expiryDate: row.expiryDate,
            createdAt: row.createdAt,
            quantity: row.quantity,
            isExpired,
          })
        }
      }
      // Sort by expiry descending (latest-expiring first) — when receiving
      // stock, the most recently entered batches are the most likely repeat.
      // No-expiry entries sink to the bottom, then fall back to createdAt desc.
      return [...byBatch.values()].sort((a, b) => {
        if (a.expiryDate && b.expiryDate) return b.expiryDate.localeCompare(a.expiryDate)
        if (a.expiryDate) return -1
        if (b.expiryDate) return 1
        return b.createdAt.localeCompare(a.createdAt)
      })
    }, [productWithStock])

    // The batch entered matches a known existing batch. When
    // this is true, we lock the expiry field to the on-file value — the
    // backend silently discards mismatched expiries on repeat receives, so we
    // enforce consistency here rather than let the user submit a value that
    // won't stick.
    const matchedBatch = useMemo(
      () => (batchNumber ? existingBatches.find((b) => b.batchNumber === batchNumber) : undefined),
      [batchNumber, existingBatches]
    )

    // Aggregate stock totals split by "this clinic" vs "other clinics" for
    // the summary header inside the autocomplete popup.
    const stockSummary = useMemo(() => {
      if (!productWithStock) return { here: 0, elsewhere: 0 }
      let here = 0
      let elsewhere = 0
      for (const row of productWithStock.stock) {
        if (clinicId && row.clinicId === clinicId) here += row.quantity
        else elsewhere += row.quantity
      }
      return { here, elsewhere }
    }, [productWithStock, clinicId])

    return (
      <div
        className={cn(
          'col-span-full grid grid-cols-subgrid items-start gap-x-3 gap-y-1',
          !isFirst && 'border-t pt-3'
        )}
      >
        {/* Product */}
        <group.Field name="product">
          {(field) => (
            <FieldWrapper
              field={field}
              layout="tabular"
              render={({ id, disabled }) => (
                <ProductSearch
                  id={id}
                  onChange={(nextProduct) => {
                    field.handleChange(nextProduct)
                    // Reset per-row inputs whenever the product changes so
                    // stale batch/expiry/qty values don't leak between products.
                    group.setFieldValue('packs', 0)
                    group.setFieldValue('looseUnits', 0)
                    group.setFieldValue('batchNumber', '')
                    group.setFieldValue('expiryDate', undefined)
                  }}
                  onBlur={field.handleBlur}
                  disabled={disabled}
                  aria-invalid={field.state.meta.errors.length > 0}
                  type={['medication', 'vaccine', 'consumable', 'service']}
                />
              )}
            />
          )}
        </group.Field>

        {/* Batch number — autocomplete over existing batches, free entry allowed */}
        <group.Field name="batchNumber">
          {(field) => (
            <FieldWrapper
              field={field}
              layout="tabular"
              render={({ id, disabled }) => (
                <Autocomplete<BatchSuggestion>
                  value={field.state.value ?? ''}
                  onValueChange={(v) => {
                    // Detect matched→unmatched transitions before mutating the
                    // field so we can clear the previously locked expiry
                    // without clobbering a user's manual entry.
                    const previous = field.state.value
                    const wasMatched =
                      !!previous && existingBatches.some((b) => b.batchNumber === previous)
                    field.handleChange(v)
                    const match = existingBatches.find((b) => b.batchNumber === v)
                    if (match) {
                      // Force-set expiry to the on-file value. The expiry
                      // field is disabled while a match is active (see below).
                      group.setFieldValue('expiryDate', match.expiryDate ?? undefined)
                    } else if (wasMatched) {
                      // Editing out of a match — the locked expiry is stale.
                      group.setFieldValue('expiryDate', undefined)
                    }
                  }}
                  items={existingBatches}
                  itemToStringValue={(b) => b.batchNumber}
                  mode="list"
                  disabled={disabled}
                  openOnInputClick
                >
                  <AutocompleteInput
                    id={id}
                    placeholder="LOT123456"
                    onBlur={field.handleBlur}
                    aria-invalid={field.state.meta.errors.length > 0}
                    className="w-full"
                    showTrigger={false}
                  />
                  <AutocompleteContent>
                    {productWithStock && (stockSummary.here > 0 || stockSummary.elsewhere > 0) && (
                      <div className="border-b px-2 py-1.5 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {stockSummary.here} in stock
                        </span>
                        {stockSummary.elsewhere > 0 && (
                          <span> ({stockSummary.elsewhere} at other clinics)</span>
                        )}
                      </div>
                    )}
                    <AutocompleteList>
                      <AutocompleteEmpty>No previous batches for this product.</AutocompleteEmpty>
                      <AutocompleteCollection>
                        {(batch) => {
                          const status = batch.isExpired
                            ? batch.quantity > 0
                              ? 'expired'
                              : 'expired_depleted'
                            : batch.quantity > 0
                              ? 'valid'
                              : 'depleted'
                          const textClass = stockRowTextClass(status)
                          const expiryLabel = formatExpiry(batch.expiryDate)
                          return (
                            <AutocompleteItem key={batch.batchNumber} value={batch}>
                              <div className="flex w-full items-center gap-2">
                                {batch.isExpired ? (
                                  <Clock className={cn('size-3.5 shrink-0', textClass)} />
                                ) : (
                                  <Package className={cn('size-3.5 shrink-0', textClass)} />
                                )}
                                <div className="flex flex-1 flex-col gap-0.5">
                                  <span className="truncate font-mono text-sm font-medium">
                                    {batch.batchNumber}
                                  </span>
                                  {expiryLabel && (
                                    <span
                                      className={cn(
                                        'text-xs text-muted-foreground',
                                        batch.isExpired && 'font-medium text-destructive-fg'
                                      )}
                                    >
                                      {batch.isExpired
                                        ? `Expired ${expiryLabel}`
                                        : `Expires ${expiryLabel}`}
                                    </span>
                                  )}
                                </div>
                                <span
                                  className={cn(
                                    'shrink-0 text-sm font-medium tabular-nums',
                                    textClass
                                  )}
                                >
                                  {formatQuantity(batch.quantity)} in stock
                                </span>
                              </div>
                            </AutocompleteItem>
                          )
                        }}
                      </AutocompleteCollection>
                    </AutocompleteList>
                  </AutocompleteContent>
                </Autocomplete>
              )}
            />
          )}
        </group.Field>

        {/* Expiry date — locked while the batch matches an existing entry */}
        <group.AppField name="expiryDate">
          {(field) => <field.DateInputField layout="tabular" disabled={!!matchedBatch} />}
        </group.AppField>

        {/* Quantity — fixed-track grid so pack/subunit inputs and their labels
            align across rows regardless of unit-label length. The subunit
            input + label collapse to empty tracks when packSize === 1. */}
        <div className="min-w-0">
          <div className="grid grid-cols-[5rem_3.5rem_5rem_auto] items-center gap-x-1.5">
            <group.Field name="packs">
              {(field) => (
                <NumericInput
                  value={field.state.value}
                  onChange={(v) => field.handleChange(v ?? 0)}
                  onBlur={field.handleBlur}
                  className="w-full"
                  placeholder="0"
                  aria-invalid={field.state.meta.errors.length > 0}
                />
              )}
            </group.Field>
            <span className="shrink-0 text-sm text-muted-foreground">{unitLabel}</span>
            {!isSingleUnit && (
              <>
                <group.Field name="looseUnits">
                  {(field) => (
                    <NumericInput
                      value={field.state.value}
                      onChange={(v) => field.handleChange(v ?? 0)}
                      onBlur={field.handleBlur}
                      className="w-full"
                      placeholder="0"
                      aria-invalid={field.state.meta.errors.length > 0}
                    />
                  )}
                </group.Field>
                <span className="shrink-0 text-sm text-muted-foreground">{subunitLabel}</span>
              </>
            )}
          </div>
          {/* Both custom "enter a quantity" errors and zod validation errors
              for the quantity fields land here. */}
          <group.Field name="packs">
            {(packsField) => (
              <group.Field name="looseUnits">
                {(looseField) => (
                  <FormError
                    errors={[...packsField.state.meta.errors, ...looseField.state.meta.errors]}
                  />
                )}
              </group.Field>
            )}
          </group.Field>
        </div>

        {/* Remove */}
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost-destructive"
            size="icon"
            type="button"
            disabled={!canRemove}
            onClick={onRemove}
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* Footer hints — rendered as extra grid cells so they auto-align. */}
        {product && (
          <>
            <span />
            <span className="col-span-2 min-w-0 text-sm text-muted-foreground">
              {matchedBatch ? (
                <span className="inline-flex items-baseline gap-1.5">
                  <Info className="size-3.5 shrink-0 self-center" />
                  <span>
                    Expiry locked to existing batch —{' '}
                    <Link
                      to={`${routes.productStock.replace(':productId', product.id)}?clinic=${clinicId ?? ''}`}
                      className="underline hover:text-foreground"
                    >
                      update on stock page
                    </Link>{' '}
                    if incorrect.
                  </span>
                </span>
              ) : (
                (() => {
                  // Non-blocking nudge when history suggests a batch number
                  // and/or expiry date but the user left them blank.
                  const missing: string[] = []
                  if (productIsBatched && !batchNumber) missing.push('a batch number')
                  if (productExpires && !expiryDate) missing.push('an expiry date')
                  if (missing.length === 0) return null
                  return (
                    <span className="inline-flex items-baseline gap-1.5 text-warning-fg">
                      <AlertTriangle className="size-3.5 shrink-0 self-center" />
                      <span>This product usually has {missing.join(' and ')}.</span>
                    </span>
                  )
                })()
              )}
            </span>
            <span className="min-w-0 text-sm text-muted-foreground">
              Total: {formatQuantity(totalUnits)}{' '}
              {product.packSize > 1 ? (
                <>
                  {product.unit} ({product.packSize} {product.unit} per {product.packUnit})
                </>
              ) : (
                product.packUnit
              )}
            </span>
            <span />
          </>
        )}
      </div>
    )
  },
})

type ReceiveStockFormProps = {
  /** Pre-populate the clinic field — pass the active clinic filter if one is set. */
  defaultClinicId?: ClinicId | null
  onSuccess?: () => void
  onCancel?: () => void
}

/**
 * Multi-row tabular form for booking stock deliveries in against one or more
 * batches. All rows share a single clinic. POSTs to /stock/receive once per
 * row — the backend handles upsert logic based on batch number.
 */
export function ReceiveStockForm({ defaultClinicId, onSuccess, onCancel }: ReceiveStockFormProps) {
  const { mutateAsync: receiveStock } = usePostStockReceive()

  const form = useAppForm({
    defaultValues: {
      clinicId: defaultClinicId ?? null,
      items: [{ ...ITEM_DEFAULTS }],
    } satisfies FormValues,
    validators: {
      // TanStack Form's Standard Schema integration requires the schema's input
      // type to match defaultValues exactly. Our product field starts as null
      // (no selection) but the schema correctly requires a non-null product, so
      // we validate manually to bridge the mismatch.
      onSubmit: ({ value }) => {
        const result = formSchema.safeParse(value)
        if (!result.success) return { fields: zodErrorToFieldErrors(result.error).fields }
      },
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.parse(value)
      try {
        for (const item of parsed.items) {
          const packSize = item.product?.packSize ?? 1
          await receiveStock({
            clinicId: parsed.clinicId,
            productId: item.product.id,
            quantityReceived: toTotalUnits(item.packs, item.looseUnits, packSize),
            batchNumber: item.batchNumber || undefined,
            expiryDate: item.expiryDate,
          })
        }
        form.reset()
        onSuccess?.()
      } catch (err) {
        form.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to receive stock',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          {/* Clinic — shared across all rows. Region-scoped picker; ClinicSearch
              itself will clear the value if the user switches to a region that
              doesn't include the current selection. */}
          <form.Field name="clinicId">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Clinic"
                required
                layoutClassName="max-w-sm"
                render={({ id, disabled }) => (
                  <ClinicSearchWithDefault
                    id={id}
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    disabled={disabled}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                )}
              />
            )}
          </form.Field>

          {/* Stock rows — single CSS grid, each row uses subgrid to align. */}
          <form.Field name="items" mode="array">
            {(arrayField) => (
              <div className={cn('grid gap-x-3 gap-y-2', GRID_TEMPLATE)}>
                {/* Column headers */}
                <div className="col-span-full grid grid-cols-subgrid items-center gap-x-3 border-b pb-2 font-medium text-muted-foreground">
                  <span>Product</span>
                  <span>Batch number</span>
                  <span>Expiry date</span>
                  <span>Quantity</span>
                  <span />
                </div>

                <form.Subscribe selector={(s) => s.values.clinicId}>
                  {(clinicId) =>
                    arrayField.state.value.map((_, i) => (
                      <ItemRow
                        // biome-ignore lint/suspicious/noArrayIndexKey: TanStack Form arrays are index-based
                        key={i}
                        form={form}
                        fields={`items[${i}]`}
                        onRemove={() => arrayField.removeValue(i)}
                        canRemove={arrayField.state.value.length > 1}
                        isFirst={i === 0}
                        clinicId={clinicId}
                      />
                    ))
                  }
                </form.Subscribe>

                <Button
                  type="button"
                  variant="outline"
                  className="col-span-full mt-1 border-dashed"
                  onClick={() => arrayField.pushValue({ ...ITEM_DEFAULTS })}
                >
                  + Add Item
                </Button>
              </div>
            )}
          </form.Field>
        </div>

        <div className="flex items-center justify-end gap-x-2 pt-3">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.Subscribe selector={(s) => s.values.items.length}>
            {(count) => (
              <form.SubmitButton>
                Receive {count} {count === 1 ? 'Batch' : 'Batches'}
              </form.SubmitButton>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}
